import React, { useState, useEffect } from 'react';
import { RotateCw, Check, X, Clock, Brain, Repeat, Zap } from 'lucide-react';
import { db, doc, getDoc, updateDoc } from './firebase';

const pythonFlashcards = [
  { id: 1, front: 'print()', back: 'Gibt Text oder Variablen auf der Konsole aus.\nBeispiel: print("Hallo")' },
  { id: 2, front: 'Variable', back: 'Ein Speicherplatz für Daten.\nBeispiel: x = 5' },
  { id: 3, front: 'String', back: 'Eine Textkette, wird in Anführungszeichen gesetzt.\nBeispiel: "Hallo Welt"' },
  { id: 4, front: 'Integer', back: 'Eine ganze Zahl ohne Komma.\nBeispiel: 42' },
  { id: 5, front: 'Float', back: 'Eine Kommazahl.\nBeispiel: 3.14' },
  { id: 6, front: 'if / else', back: 'Bedingte Anweisung. Führt Code nur aus, wenn eine Bedingung wahr ist.' },
  { id: 7, front: 'for Schleife', back: 'Wiederholt Code für jedes Element in einer Sequenz.' },
  { id: 8, front: 'while Schleife', back: 'Wiederholt Code, solange eine Bedingung wahr ist.' },
  { id: 9, front: 'Liste', back: 'Eine geordnete Sammlung von Elementen.\nBeispiel: [1, 2, 3]' },
  { id: 10, front: 'Kommentar', back: 'Text, der vom Computer ignoriert wird. Startet mit #.' },
  { id: 11, front: 'Boolean', back: 'Ein Wahrheitswert: True oder False.' },
  { id: 12, front: 'input()', back: 'Liest eine Eingabe vom Benutzer ein.' },
  { id: 13, front: 'len()', back: 'Gibt die Länge einer Liste oder eines Strings zurück.\nBeispiel: len("Hallo") -> 5' },
  { id: 14, front: 'type()', back: 'Zeigt den Datentyp eines Wertes an.\nBeispiel: type(5) -> <class \'int\'>' },
  { id: 15, front: 'str()', back: 'Wandelt einen Wert in einen String um.\nBeispiel: str(10) -> "10"' },
  { id: 16, front: 'int()', back: 'Wandelt einen Wert in eine ganze Zahl um.\nBeispiel: int("5") -> 5' },
  { id: 17, front: 'range()', back: 'Erzeugt eine Zahlenfolge, oft für Schleifen genutzt.\nBeispiel: range(3) -> 0, 1, 2' },
  { id: 18, front: 'def', back: 'Schlüsselwort, um eine eigene Funktion zu definieren.\nBeispiel: def sag_hallo():' },
  { id: 19, front: 'return', back: 'Gibt einen Wert aus einer Funktion zurück und beendet sie.' },
  { id: 20, front: 'and / or / not', back: 'Logische Operatoren um Bedingungen zu verknüpfen.' },
  { id: 21, front: '==', back: 'Vergleichsoperator: Prüft ob zwei Werte gleich sind.\n(Nicht verwechseln mit = für Zuweisung)' },
  { id: 22, front: 'Dictionary', back: 'Speichert Daten in Schlüssel-Wert-Paaren.\nBeispiel: {"Name": "Max", "Alter": 10}' },
  { id: 23, front: 'Tuple', back: 'Wie eine Liste, aber unveränderbar (immutable).\nBeispiel: (1, 2, 3)' },
  { id: 24, front: 'import', back: 'Lädt externe Module oder Bibliotheken.\nBeispiel: import math' },
];

const javascriptFlashcards = [
  { id: 101, front: 'console.log()', back: 'Gibt Text auf der Konsole aus.\nBeispiel: console.log("Hallo")' },
  { id: 102, front: 'let', back: 'Erstellt eine Variable, die man ändern kann.\nBeispiel: let x = 5;' },
  { id: 103, front: 'const', back: 'Erstellt eine Variable, die man NICHT ändern kann (Konstante).\nBeispiel: const pi = 3.14;' },
  { id: 104, front: 'String', back: 'Text. Wird in " oder \' oder ` gesetzt.\nBeispiel: "Hallo"' },
  { id: 105, front: 'Number', back: 'Zahlen (ganz oder Komma).\nBeispiel: 42 oder 3.14' },
  { id: 106, front: 'Boolean', back: 'Wahrheitswert: true oder false.' },
  { id: 107, front: 'if / else', back: 'Führt Code nur aus, wenn eine Bedingung wahr ist.\nBeispiel: if (x > 5) { ... }' },
  { id: 108, front: '===', back: 'Vergleich: Ist Wert UND Typ gleich?\n(Besser als ==)' },
  { id: 109, front: '!==', back: 'Ungleichheit (strikt).' },
  { id: 110, front: '&&', back: 'Logisches UND.\nBeispiel: true && false -> false' },
  { id: 111, front: '||', back: 'Logisches ODER.\nBeispiel: true || false -> true' },
  { id: 112, front: '!', back: 'Logisches NICHT (Gegenteil).\nBeispiel: !true -> false' },
  { id: 113, front: 'Array', back: 'Eine Liste von Werten.\nBeispiel: [1, 2, 3]' },
  { id: 114, front: 'Object', back: 'Daten mit Eigenschaften.\nBeispiel: { name: "Max", alter: 10 }' },
  { id: 115, front: 'function', back: 'Erstellt eine Funktion.\nBeispiel: function hallo() { ... }' },
  { id: 116, front: 'return', back: 'Gibt einen Wert aus einer Funktion zurück.' },
  { id: 117, front: '//', back: 'Einzeiliger Kommentar.' },
  { id: 118, front: 'length', back: 'Gibt die Länge von Strings oder Arrays.\nBeispiel: "Hi".length -> 2' },
  { id: 119, front: 'undefined', back: 'Wert einer Variable, die noch keinen Wert hat.' },
  { id: 120, front: 'null', back: 'Absichtlicher "leerer" Wert.' },
];

const htmlFlashcards = [
  { id: 201, front: '<html>', back: 'Das Wurzelelement einer HTML-Seite.' },
  { id: 202, front: '<head>', back: 'Enthält Metadaten (z.B. Titel, CSS-Links).' },
  { id: 203, front: '<body>', back: 'Enthält den sichtbaren Inhalt der Seite.' },
  { id: 204, front: '<h1>', back: 'Die wichtigste Überschrift.' },
  { id: 205, front: '<p>', back: 'Ein Textabsatz (Paragraph).' },
  { id: 206, front: '<a>', back: 'Ein Link (Anchor). Braucht href="...".' },
  { id: 207, front: '<img>', back: 'Ein Bild. Braucht src="...".' },
  { id: 208, front: '<div>', back: 'Ein Container für Layout-Bereiche.' },
  { id: 209, front: '<ul>', back: 'Ungeordnete Liste (Punkte).' },
  { id: 210, front: '<ol>', back: 'Geordnete Liste (Zahlen).' },
  { id: 211, front: '<li>', back: 'Ein Listeneintrag.' },
  { id: 212, front: '<input>', back: 'Ein Eingabefeld.' },
  { id: 213, front: '<button>', back: 'Ein klickbarer Knopf.' },
  { id: 214, front: 'class', back: 'Attribut für CSS-Klassen.' },
  { id: 215, front: 'id', back: 'Eindeutige ID für ein Element.' },
  { id: 216, front: 'src', back: 'Quelle für Bilder/Skripte.' },
  { id: 217, front: 'href', back: 'Ziel-URL für Links.' },
  { id: 218, front: '<br>', back: 'Zeilenumbruch.' },
  { id: 219, front: '<table>', back: 'Erstellt eine Tabelle.' },
  { id: 220, front: '<form>', back: 'Container für Formulare.' },
];

const kotlinFlashcards = [
  { id: 301, front: 'fun', back: 'Schlüsselwort, um eine eigene Funktion zu definieren.\nBeispiel: fun main() { ... }' },
  { id: 302, front: 'val', back: 'Deklariert eine Variable, die NICHT mehr geändert werden kann (immutable).\nBeispiel: val pi = 3.14' },
  { id: 303, front: 'var', back: 'Deklariert eine Variable, die geändert werden kann (mutable).\nBeispiel: var count = 0' },
  { id: 304, front: 'println()', back: 'Gibt Text auf der Konsole aus und fügt einen Zeilenumbruch hinzu.' },
  { id: 305, front: 'print()', back: 'Gibt Text auf der Konsole aus OHNE einen Zeilenumbruch hinzuzufügen.' },
  { id: 306, front: 'String', back: 'Datentyp für Text.\nBeispiel: val name: String = "Duo"' },
  { id: 307, front: 'Int', back: 'Datentyp für ganze Zahlen.\nBeispiel: val alter: Int = 25' },
  { id: 308, front: 'Double', back: 'Datentyp für Kommazahlen.\nBeispiel: val pi: Double = 3.1415' },
  { id: 309, front: 'Boolean', back: 'Ein Wahrheitswert: true oder false.' },
  { id: 310, front: 'if / else', back: 'Bedingte Anweisung.\nBeispiel: if (x > 0) { ... } else { ... }' },
  { id: 311, front: 'when', back: 'Mächtige Alternative zu "switch" oder langen if-else-Ketten.\nBeispiel: when (x) { 1 -> print("Eins") }' },
  { id: 312, front: 'for', back: 'Schleife, um über Elemente (z.B. Listen oder Ranges) zu iterieren.\nBeispiel: for (i in 1..5) { ... }' },
  { id: 313, front: 'while', back: 'Wiederholt Code, solange eine Bedingung wahr ist.' },
  { id: 314, front: '.. (Range)', back: 'Erstellt einen Bereich von Werten.\nBeispiel: 1..10 (Zahlen 1 bis einschließlich 10)' },
  { id: 315, front: 'arrayOf()', back: 'Erstellt ein Array mit den angegebenen Elementen.\nBeispiel: val zahlen = arrayOf(1, 2, 3)' },
  { id: 316, front: 'listOf()', back: 'Erstellt eine UNVERÄNDERBARE Liste (Read-Only).\nBeispiel: val farben = listOf("Rot", "Blau")' },
  { id: 317, front: 'mutableListOf()', back: 'Erstellt eine VERÄNDERBARE Liste (Elemente können hinzugefügt/entfernt werden).' },
  { id: 318, front: '?', back: 'Markiert einen Datentyp als "Nullable" (darf null sein).\nBeispiel: var name: String? = null' },
  { id: 319, front: '?. (Safe Call)', back: 'Führt eine Methode nur aus, wenn das Objekt nicht null ist.\nBeispiel: name?.length' },
  { id: 320, front: '?: (Elvis Operator)', back: 'Liefert einen Standardwert, falls der Ausdruck davor null ist.\nBeispiel: val len = name?.length ?: 0' },
  { id: 321, front: '!! (Not-Null)', back: 'Erzwingt, dass ein Wert nicht null ist. Wirft eine Exception, wenn er doch null ist.' },
  { id: 322, front: 'class', back: 'Definiert eine Bauanleitung (Klasse) für Objekte.\nBeispiel: class Auto(val marke: String)' },
  { id: 323, front: 'data class', back: 'Spezielle Klasse, die primär Daten hält. Erzeugt automatisch nützliche Methoden wie toString() oder copy().' },
  { id: 324, front: 'is', back: 'Überprüft, ob ein Objekt von einem bestimmten Datentyp ist.\nBeispiel: if (obj is String) { ... }' },
];

function Flashcards({ darkMode, user, activeCourse = 'Python', onCardReviewed }) {
  const [queue, setQueue] = useState([]);
  const [currentCard, setCurrentCard] = useState(null);
  const [isFlipped, setIsFlipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [progressData, setProgressData] = useState({});
  const [finished, setFinished] = useState(false);

  let flashcardsData;
  if (activeCourse === 'JavaScript') {
    flashcardsData = javascriptFlashcards;
  } else if (activeCourse === 'HTML') {
    flashcardsData = htmlFlashcards;
  } else if (activeCourse === 'Kotlin') {
    flashcardsData = kotlinFlashcards;
  } else {
    flashcardsData = pythonFlashcards;
  }

  // Daten laden und Queue erstellen
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      let data = {};

      if (user) {
        try {
          const docRef = doc(db, 'users', user.uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists() && docSnap.data().flashcards) {
            data = docSnap.data().flashcards;
          }
        } catch (e) {
          console.error("Fehler beim Laden der Flashcards:", e);
        }
      }
      setProgressData(data);

      const now = Date.now();
      // Karten filtern: Neue Karten ODER Karten, deren nextReview in der Vergangenheit liegt
      const due = flashcardsData.filter(card => {
        const cardProgress = data[card.id];
        if (!cardProgress) return true; // Neue Karte
        return cardProgress.nextReview <= now;
      });

      // Mischen
      setQueue(due.sort(() => Math.random() - 0.5));
      setLoading(false);
    };

    loadData();
  }, [user, activeCourse]);

  // Nächste Karte setzen
  useEffect(() => {
    if (!currentCard && queue.length > 0) {
      setCurrentCard(queue[0]);
      setFinished(false);
    } else if (!currentCard && queue.length === 0 && !loading) {
      setFinished(true);
    }
  }, [queue, currentCard, loading]);

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const handleRate = async (rating) => {
    if (!currentCard) return;

    // Wenn User nicht eingeloggt ist, einfach weiter
    if (!user) {
      nextCard();
      return;
    }

    const cardId = currentCard.id;
    const prev = progressData[cardId] || { interval: 0, ease: 2.5, repetitions: 0 };
    
    let newInterval = 1;
    let newEase = prev.ease;
    let newRepetitions = prev.repetitions;

    // Spaced Repetition Algorithmus (Vereinfachtes SM-2)
    if (rating === 'again') {
      newRepetitions = 0;
      newInterval = 0; // Sofort wiederholen
    } else if (rating === 'hard') {
      newInterval = Math.max(1, Math.floor(prev.interval * 1.2));
      newEase = Math.max(1.3, prev.ease - 0.15);
      newRepetitions += 1;
    } else if (rating === 'good') {
      newInterval = Math.max(1, Math.floor(prev.interval * newEase));
      newRepetitions += 1;
    } else if (rating === 'easy') {
      newInterval = Math.max(1, Math.floor(prev.interval * newEase * 1.3));
      newEase += 0.15;
      newRepetitions += 1;
    }

    if (onCardReviewed) onCardReviewed();

    // Wenn "Nochmal", Karte hinten anstellen für diese Session
    if (rating === 'again') {
      setQueue(prevQueue => [...prevQueue.slice(1), currentCard]);
      setIsFlipped(false);
      setCurrentCard(null); // Trigger useEffect
      return; 
    }

    // Speichern
    const nextReview = Date.now() + (newInterval * 24 * 60 * 60 * 1000);
    const updatedData = { interval: newInterval, ease: newEase, repetitions: newRepetitions, nextReview };
    
    // Optimistisches Update
    setProgressData(prev => ({ ...prev, [cardId]: updatedData }));
    
    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        [`flashcards.${cardId}`]: updatedData
      });
    } catch (e) {
      console.error("Fehler beim Speichern:", e);
    }

    nextCard();
  };

  const nextCard = () => {
    setIsFlipped(false);
    setTimeout(() => {
      setQueue(prev => prev.slice(1));
      setCurrentCard(null);
    }, 150);
  };

  if (loading) {
    return (
      <main className="main-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ color: darkMode ? '#fff' : '#333' }}>Lade Karten...</div>
      </main>
    );
  }

  if (finished) {
    return (
      <main className="main-content">
        <div className="main-content-header" style={{ position: 'sticky', top: '20px', zIndex: 10, backgroundColor: '#1cb0f6', borderBottom: '4px solid #148ac2', borderRadius: '16px', color: 'white', marginBottom: '20px', marginTop: '20px' }}>
          <h2>Karteikarten</h2>
          <span>Alles erledigt!</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 200px)', textAlign: 'center', color: darkMode ? '#fff' : '#333' }}>
          <div style={{ fontSize: '80px', marginBottom: '20px' }}>🎉</div>
          <h2 style={{ fontSize: '2rem', margin: '0 0 10px 0' }}>Klasse gemacht!</h2>
          <p style={{ fontSize: '1.2rem', opacity: 0.8, maxWidth: '400px' }}>Du hast alle fälligen Karten für heute gelernt. Komm morgen wieder!</p>
          <button 
            onClick={() => {
              if (navigator.vibrate) navigator.vibrate(10);
              setQueue([...flashcardsData].sort(() => Math.random() - 0.5));
              setFinished(false);
            }}
            style={{
              marginTop: '30px',
              padding: '15px 30px',
              backgroundColor: '#58cc02',
              color: 'white',
              border: 'none',
              borderRadius: '16px',
              fontSize: '1.1rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 4px 0 #46a302',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}
          >
            <RotateCw size={24} /> Trotzdem üben
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="main-content">
      <div className="main-content-header" style={{ position: 'sticky', top: '20px', zIndex: 10, backgroundColor: '#1cb0f6', borderBottom: '4px solid #148ac2', borderRadius: '16px', color: 'white', marginBottom: '20px', marginTop: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2>Karteikarten</h2>
            <span>{activeCourse === 'HTML' ? 'HTML Tags & Attribute' : `${activeCourse} Syntax & Befehle`}</span>
          </div>
          <div style={{ backgroundColor: 'rgba(255,255,255,0.2)', padding: '5px 12px', borderRadius: '12px', fontSize: '0.9rem', fontWeight: 'bold' }}>
            {queue.length} verbleibend
          </div>
        </div>
      </div>

      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: 'calc(100vh - 200px)',
        padding: '20px'
      }}>
        <div 
          className={`flashcard ${isFlipped ? 'flipped' : ''}`} 
          onClick={() => { if (navigator.vibrate) navigator.vibrate(10); !isFlipped && handleFlip(); }}
          style={{
            width: '100%',
            maxWidth: '500px',
            height: '300px',
            perspective: '1000px',
            cursor: 'pointer',
            position: 'relative'
          }}
        >
          <div className="flashcard-inner" style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            textAlign: 'center',
            transition: 'transform 0.6s',
            transformStyle: 'preserve-3d',
            transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
          }}>
            {/* Front */}
            <div className="flashcard-front" style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              backfaceVisibility: 'hidden',
              backgroundColor: '#1cb0f6',
              color: 'white',
              borderRadius: '20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 0 #148ac2, 0 10px 20px rgba(0,0,0,0.2)',
              padding: '20px',
              boxSizing: 'border-box'
            }}>
              <h3 style={{ fontSize: '2.5rem', margin: 0, fontWeight: '800' }}>{currentCard?.front}</h3>
              <div style={{ marginTop: '20px', opacity: 0.8, display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.9rem' }}>
                <RotateCw size={16} /> Klicken zum Umdrehen
              </div>
            </div>

            {/* Back */}
            <div className="flashcard-back" style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              backfaceVisibility: 'hidden',
              backgroundColor: darkMode ? '#2b2b2b' : 'white',
              color: darkMode ? 'white' : '#333',
              borderRadius: '20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: darkMode ? '0 8px 0 #444, 0 10px 20px rgba(0,0,0,0.4)' : '0 8px 0 #e5e5e5, 0 10px 20px rgba(0,0,0,0.1)',
              transform: 'rotateY(180deg)',
              padding: '30px',
              border: darkMode ? '2px solid #444' : '2px solid #e5e5e5',
              boxSizing: 'border-box'
            }}>
              <p style={{ fontSize: '1.3rem', lineHeight: '1.6', whiteSpace: 'pre-line', fontWeight: '500' }}>{currentCard?.back}</p>
            </div>
          </div>
        </div>

        {isFlipped && (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr 1fr 1fr', 
            gap: '10px', 
            marginTop: '40px',
            width: '100%',
            maxWidth: '600px'
          }}>
            <button onClick={() => { if (navigator.vibrate) navigator.vibrate(10); handleRate('again'); }} className="srs-button" style={{ backgroundColor: '#ff4b4b', borderBottom: '4px solid #d32f2f' }}>
              <div style={{ fontSize: '0.8rem', opacity: 0.8, marginBottom: '2px' }}>&lt; 1m</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><Repeat size={18} /> Nochmal</div>
            </button>
            
            <button onClick={() => { if (navigator.vibrate) navigator.vibrate(10); handleRate('hard'); }} className="srs-button" style={{ backgroundColor: '#ff9600', borderBottom: '4px solid #cc7a00' }}>
              <div style={{ fontSize: '0.8rem', opacity: 0.8, marginBottom: '2px' }}>2t</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><Clock size={18} /> Schwer</div>
            </button>
            
            <button onClick={() => { if (navigator.vibrate) navigator.vibrate(10); handleRate('good'); }} className="srs-button" style={{ backgroundColor: '#1cb0f6', borderBottom: '4px solid #148ac2' }}>
              <div style={{ fontSize: '0.8rem', opacity: 0.8, marginBottom: '2px' }}>4t</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><Check size={18} /> Gut</div>
            </button>
            
            <button onClick={() => { if (navigator.vibrate) navigator.vibrate(10); handleRate('easy'); }} className="srs-button" style={{ backgroundColor: '#58cc02', borderBottom: '4px solid #46a302' }}>
              <div style={{ fontSize: '0.8rem', opacity: 0.8, marginBottom: '2px' }}>7t</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><Zap size={18} /> Einfach</div>
            </button>
          </div>
        )}
        
        <style>{`
          .srs-button {
            border: none;
            border-radius: 12px;
            padding: 12px 5px;
            color: white;
            font-weight: 800;
            cursor: pointer;
            transition: transform 0.1s;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          }
          .srs-button:active {
            transform: translateY(4px);
            border-bottom-width: 0 !important;
            margin-top: 4px;
          }
        `}</style>
      </div>
    </main>
  );
}

export default Flashcards;
